import { parseAccountIdsFromString } from '@/helpers/parseAccountIdsFromString';
import { createAccountIdsValidator } from '@/helpers/validateAccountIds';
import {
  Box,
  Button,
  FormikRadioGroup,
  FormikSelectInputNative,
  FormikTextareaInput,
  FormikTextInput,
  toast,
} from '@palmetto/palmetto-components';
import { Field, Form, Formik } from 'formik';
import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { object, string, Schema } from 'yup';

import {
  useAddBillingBlocksMutation,
  useRemoveBillingBlocksMutation,
  useGetBillingBlockSettingsQuery,
} from '../../services/billingBlocks';
import { isErrorWithData } from '../../services/helpers';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';

import { BillingBlockResult } from './BillingBlockResult';

interface BillingBlockFormData {
  action: 'add' | 'remove' | '';
  accountIds: string;
  reason: string;
  notes: string;
}

export function BillingBlockAdmin() {
  const [results, setResults] = useState<any>();
  const [addBillingBlocks] = useAddBillingBlocksMutation();
  const [removeBillingBlocks] = useRemoveBillingBlocksMutation();
  const {
    data: settingsData,
    isLoading: isLoadingSettings,
    error: settingsError,
  } = useGetBillingBlockSettingsQuery();

  const reasonOptions = useMemo(() => {
    if (!settingsData?.reasons) {
      return [];
    }
    const filtered = settingsData.reasons.filter((reason) => reason.show);
    return filtered.map((reason) => ({
      value: reason.id,
      label: reason.label,
    }));
  }, [settingsData]);

  const billingBlockSchema = useMemo(() => {
    const reasonIds = settingsData?.reasons.map((r) => r.id) || [];
    return {
      action: string().required('Select an action').oneOf(['add', 'remove']),
      accountIds: string()
        .required('Account IDs are required')
        .test('is-valid-mongo-ids', 'Invalid account IDs', createAccountIdsValidator()),
      reason: string()
        .required('Reason is required')
        .oneOf(reasonIds.length > 0 ? reasonIds : [''], 'Invalid reason selected'),
      notes: string().required('Notes are required'),
    };
  }, [settingsData]);

  const BillingBlockFormSchema: Schema<BillingBlockFormData> = useMemo(
    () => object().shape(billingBlockSchema).noUnknown(true),
    [billingBlockSchema],
  );

  const handleSubmit = async (values: BillingBlockFormData, { setSubmitting, resetForm }: any) => {
    const loadingToastId = toast.loading('Processing accounts...');
    try {
      let accountIds = await parseAccountIdsFromString(values.accountIds);
      accountIds = accountIds.filter((id: string) => id.trim().length > 0);

      let response;
      if (values.action === 'add') {
        response = await addBillingBlocks({
          accountIds,
          reason: values.reason,
          notes: values.notes,
        }).unwrap();
      } else {
        response = await removeBillingBlocks({
          accountIds,
          reason: values.reason,
          notes: values.notes,
        }).unwrap();
      }

      setResults({
        ...response,
        reasonLabel: reasonOptions.find((r) => r.value === values.reason)?.label,
      });
      toast.dismiss(loadingToastId);
      toast.success(
        `Successfully processed ${response.completedAccounts.length} account${response.completedAccounts.length !== 1 ? 's' : ''}`,
      );
      resetForm();
    } catch (error: any) {
      toast.dismiss(loadingToastId);
      if (isErrorWithData(error)) {
        toast.error(error.data.message || 'Failed to process billing blocks');
      } else {
        toast.error('An unexpected error occurred');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoadingSettings) {
    return (
      <>
        <Helmet>
          <title>Block Billing</title>
        </Helmet>
        <PageHeader title="Block Billing" />
        <MainContainer>
          <Box padding="lg">Loading settings...</Box>
        </MainContainer>
      </>
    );
  }

  if (settingsError) {
    return (
      <>
        <Helmet>
          <title>Block Billing</title>
        </Helmet>
        <PageHeader title="Block Billing" />
        <MainContainer>
          <Box padding="lg" background="white" borderRadius="md" shadow="sm">
            <Box color="danger-500">
              Error loading billing block settings. Please make sure the migration has been run.
            </Box>
            <Box margin="md 0 0 0">
              <pre>{JSON.stringify(settingsError, null, 2)}</pre>
            </Box>
          </Box>
        </MainContainer>
      </>
    );
  }

  if (!settingsData?.reasons || reasonOptions.length === 0) {
    return (
      <>
        <Helmet>
          <title>Block Billing</title>
        </Helmet>
        <PageHeader title="Block Billing" />
        <MainContainer>
          <Box padding="lg" background="white" borderRadius="md" shadow="sm">
            <Box color="warning-500">
              No billing block reasons are configured. Please check the database settings.
            </Box>
            <Box margin="md 0 0 0">Settings data: {JSON.stringify(settingsData, null, 2)}</Box>
          </Box>
        </MainContainer>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Block Billing</title>
      </Helmet>
      <PageHeader title="Block Billing" />
      <MainContainer>
        <Box padding="lg" background="white" borderRadius="md" shadow="sm">
          <Formik
            initialValues={{
              action: '' as 'add' | 'remove' | '',
              accountIds: '',
              reason: '',
              notes: '',
            }}
            validationSchema={BillingBlockFormSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values }) => (
              <Form>
                <FormSection title="Billing Block Action">
                  <Box childGap="md">
                    <Field
                      name="action"
                      id="action"
                      component={FormikRadioGroup}
                      label="Action"
                      options={[
                        { id: 'add', value: 'add', label: 'Add Billing Block' },
                        { id: 'remove', value: 'remove', label: 'Remove Billing Block' },
                      ]}
                      isRequired
                    />

                    <Field
                      name="accountIds"
                      id="accountIds"
                      component={FormikTextareaInput}
                      label="Account IDs"
                      helpText="Enter account IDs separated by commas or on separate lines"
                      rows={8}
                      isRequired
                    />

                    <Field
                      name="reason"
                      id="reason"
                      component={FormikSelectInputNative}
                      label="Reason"
                      options={reasonOptions}
                      isRequired
                    />

                    <Field
                      name="notes"
                      id="notes"
                      component={FormikTextInput}
                      label="Notes"
                      helpText="Notes about this billing block action"
                      isRequired
                    />

                    <Box display="flex" gap="md" justifyContent="flex-end">
                      <Button type="submit" variant="primary" isLoading={isSubmitting} isDisabled={isSubmitting}>
                        {values.action === 'add' ? 'Add Billing Blocks' : 'Remove Billing Blocks'}
                      </Button>
                    </Box>
                  </Box>
                </FormSection>
              </Form>
            )}
          </Formik>
        </Box>

        {results && (
          <Box margin="lg 0 0 0">
            <BillingBlockResult results={results} reasonLabel={results.reasonLabel} />
          </Box>
        )}
      </MainContainer>
    </>
  );
}
